import React from "react";
import { Download, Plus, Minus, Maximize2 } from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { API } from "../lib/api";
import { getVisitorId } from "../lib/analytics";

// Money-flow schematic for the Solutions page ("the real beauty ...").
// Hand-built SVG so structure/ranks are exact. Only the four app names appear
// as text; everything else is icons/arrows. Light "poster" card for dark mode.

const APP = {
  familigi: { img: "/solutions/family_tree.png" },
  boxalong: { img: "/solutions/holdings.png" },
  currencida: { img: "/solutions/currency.png" },
  sebaa: { img: "/solutions/benefits_sq.png" },
};

// each app node deep-links to its product card on the Solutions page
const APP_SLUG = {
  familigi: "family-tree-application",
  boxalong: "holdings-management-application",
  currencida: "currency-management-application",
  sebaa: "benefits-management-application",
};

const C = {
  ink: "#0f172a", line: "#334155", green: "#16a34a", greenDark: "#15803d",
  black: "#111827", red: "#dc2626", seal: "#dc2626", yellow: "#eab308",
  blue: "#2563eb", node: "#ffffff",
};

function LogoNode({ cx, cy, app, size = 92, label, labelBelow = true, onNavigate }) {
  const a = APP[app];
  const x = cx - size / 2, y = cy - size / 2, cid = `clip-${app}`;
  const slug = APP_SLUG[app];
  const clickable = !!(onNavigate && slug);
  return (
    <g onClick={clickable ? () => onNavigate(slug) : undefined}
      style={clickable ? { cursor: "pointer" } : undefined}
      data-testid={clickable ? `infographic-node-${app}` : undefined}>
      {clickable && <title>Open {label} details</title>}
      <defs><clipPath id={cid}><rect x={x} y={y} width={size} height={size} rx={16} /></clipPath></defs>
      <rect x={x - 3} y={y - 3} width={size + 6} height={size + 6} rx={19} fill="#ffffff" stroke={C.greenDark} strokeWidth="2" />
      <image href={a.img} x={x} y={y} width={size} height={size} clipPath={`url(#${cid})`} preserveAspectRatio="xMidYMid slice" />
      {label && (
        <text x={cx} y={labelBelow ? cy + size / 2 + 20 : cy - size / 2 - 10} textAnchor="middle"
          fontSize="15" fontWeight="800" fill={C.ink}
          style={clickable ? { textDecoration: "underline" } : undefined}>{label}</text>
      )}
    </g>
  );
}

function TNode({ x, y, rank, w = 44 }) {
  const h = 26;
  return (
    <g>
      <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={5} fill={C.node} stroke={C.line} strokeWidth="1.2" />
      {rank != null && (<g>
        <circle cx={x - w / 2} cy={y - h / 2} r="8" fill={C.black} />
        <text x={x - w / 2} y={y - h / 2 + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{rank}</text>
      </g>)}
    </g>
  );
}

const link = (x1, y1, x2, y2, color = C.line, w = 1.4) => {
  const my = (y1 + y2) / 2;
  return <path d={`M ${x1} ${y1} V ${my} H ${x2} V ${y2}`} fill="none" stroke={color} strokeWidth={w} />;
};

/* ---- asset icons (no text) ---------------------------------------------- */
function StatelyHome({ cx, cy }) {
  const winCols = [-62, -48, -34, 29, 43, 57], winRows = [cy - 14, cy + 8];
  return (
    <g stroke="#6b7280" strokeWidth="1.1">
      <rect x={cx - 88} y={cy + 42} width={176} height={4} fill="#86b06b" stroke="none" />
      <rect x={cx - 72} y={cy - 20} width={144} height={62} fill="#e8e3d6" />
      <rect x={cx - 75} y={cy - 26} width={150} height={7} fill="#cfc7b5" />
      {Array.from({ length: 15 }).map((_, i) => (<line key={i} x1={cx - 70 + i * 10} y1={cy - 26} x2={cx - 70 + i * 10} y2={cy - 20} stroke="#b8ae9c" strokeWidth="1.4" />))}
      {[-58, -42, 41, 57].map((dx, i) => (<rect key={i} x={cx + dx} y={cy - 36} width={8} height={11} fill="#8a7f6b" stroke="#6b7280" />))}
      <rect x={cx - 18} y={cy - 30} width={36} height={72} fill="#efeade" />
      <polygon points={`${cx - 25},${cy - 30} ${cx},${cy - 49} ${cx + 25},${cy - 30}`} fill="#d6cfc0" />
      <path d={`M ${cx - 8} ${cy - 49} a 8 8 0 0 1 16 0 Z`} fill="#cfc7b5" />
      <line x1={cx} y1={cy - 57} x2={cx} y2={cy - 63} stroke="#6b7280" strokeWidth="1.2" />
      <rect x={cx - 8} y={cy + 20} width={16} height={22} fill="#5b4636" />
      <rect x={cx - 6} y={cy - 12} width={12} height={16} fill="#334155" />
      {winCols.map((dx) => winRows.map((wy, ri) => (
        <g key={`${dx}-${ri}`}>
          <rect x={cx + dx} y={wy} width={9} height={15} fill="#334155" />
          <line x1={cx + dx + 4.5} y1={wy} x2={cx + dx + 4.5} y2={wy + 15} stroke="#e8e3d6" strokeWidth="0.7" />
          <line x1={cx + dx} y1={wy + 7.5} x2={cx + dx + 9} y2={wy + 7.5} stroke="#e8e3d6" strokeWidth="0.7" />
        </g>
      )))}
    </g>
  );
}
function Plane({ cx, cy }) {
  const windows = [];
  for (let x = 46; x >= -44; x -= 8) windows.push(x);
  return (
    <g stroke={C.line} strokeWidth="1.1">
      <path d={`M ${cx + 22} ${cy + 8} L ${cx - 44} ${cy + 42} L ${cx - 18} ${cy + 42} L ${cx + 44} ${cy + 8} Z`} fill="#94a3b8" />
      <path d={`M ${cx + 22} ${cy - 8} L ${cx - 44} ${cy - 42} L ${cx - 18} ${cy - 42} L ${cx + 44} ${cy - 8} Z`} fill="#cbd5e1" />
      <path d={`M ${cx - 66} ${cy - 7} L ${cx - 90} ${cy - 44} L ${cx - 76} ${cy - 45} L ${cx - 52} ${cy - 7} Z`} fill="#94a3b8" />
      <path d={`M ${cx - 100} ${cy - 42} L ${cx - 64} ${cy - 47} L ${cx - 67} ${cy - 43} L ${cx - 97} ${cy - 38} Z`} fill="#cbd5e1" />
      <path d={`M ${cx - 82} ${cy - 4} L ${cx - 28} ${cy - 12} L ${cx + 54} ${cy - 12} Q ${cx + 84} ${cy - 9} ${cx + 91} ${cy + 1} Q ${cx + 84} ${cy + 12} ${cx + 54} ${cy + 13} L ${cx - 46} ${cy + 13} Q ${cx - 76} ${cy + 11} ${cx - 82} ${cy - 4} Z`} fill="#f8fafc" />
      <ellipse cx={cx - 40} cy={cy - 6} rx={17} ry={8.5} fill="#64748b" />
      <ellipse cx={cx - 55} cy={cy - 6} rx={3.5} ry={7} fill="#334155" stroke="none" />
      <path d={`M ${cx - 30} ${cy - 3} L ${cx - 22} ${cy - 8} L ${cx - 22} ${cy - 2} Z`} fill="#64748b" />
      {windows.map((dx, i) => (<circle key={i} cx={cx + dx} cy={cy - 3.5} r={1.9} fill="#1e3a8a" stroke="none" />))}
      <path d={`M ${cx + 56} ${cy - 8} L ${cx + 76} ${cy - 4} L ${cx + 74} ${cy + 1} L ${cx + 56} ${cy - 2} Z`} fill="#1e3a8a" stroke="none" />
    </g>
  );
}
function Boat({ cx, cy }) {
  const d1 = [-50, -38, -26, -14, -2, 10, 22], d2 = [-44, -32, -20, -8, 4], d3 = [-38, -28, -18, -8];
  return (
    <g stroke={C.line} strokeWidth="1.1">
      <path d={`M ${cx - 64} ${cy + 8} L ${cx + 52} ${cy + 8} L ${cx + 76} ${cy + 1} L ${cx + 60} ${cy + 22} L ${cx - 56} ${cy + 22} Z`} fill="#1e293b" />
      <line x1={cx - 60} y1={cy + 11} x2={cx + 66} y2={cy + 6} stroke="#38bdf8" strokeWidth="1.4" />
      <path d={`M ${cx - 56} ${cy + 8} L ${cx - 56} ${cy - 6} L ${cx + 28} ${cy - 6} L ${cx + 44} ${cy + 8} Z`} fill="#f8fafc" />
      <path d={`M ${cx - 50} ${cy - 6} L ${cx - 50} ${cy - 18} L ${cx + 16} ${cy - 18} L ${cx + 30} ${cy - 6} Z`} fill="#eef2f6" />
      <path d={`M ${cx - 44} ${cy - 18} L ${cx - 44} ${cy - 29} L ${cx + 2} ${cy - 29} L ${cx + 14} ${cy - 18} Z`} fill="#e2e8f0" />
      <path d={`M ${cx - 40} ${cy - 29} Q ${cx - 22} ${cy - 46} ${cx - 4} ${cy - 29}`} fill="none" stroke={C.line} strokeWidth="2" />
      <line x1={cx - 22} y1={cy - 41} x2={cx - 22} y2={cy - 50} stroke={C.line} strokeWidth="1.4" />
      {d1.map((dx, i) => <rect key={`a${i}`} x={cx + dx} y={cy - 2} width={8} height={5} fill="#38bdf8" stroke="none" />)}
      {d2.map((dx, i) => <rect key={`b${i}`} x={cx + dx} y={cy - 14} width={7} height={5} fill="#38bdf8" stroke="none" />)}
      {d3.map((dx, i) => <rect key={`c${i}`} x={cx + dx} y={cy - 25} width={6} height={4} fill="#7dd3fc" stroke="none" />)}
      <path d={`M ${cx - 70} ${cy + 27} q 9 4 18 0 q 9 -4 18 0 q 9 4 18 0 q 9 -4 18 0 q 9 4 18 0 q 9 -4 18 0`} fill="none" stroke="#38bdf8" strokeWidth="1.4" />
    </g>
  );
}
function Factory({ cx, cy }) {
  return (
    <g stroke={C.line} strokeWidth="1.3">
      {[-24, -2, 20].map((dx, i) => (<g key={i}>
        <rect x={cx + dx} y={cy - 30} width={12} height={30} fill="#64748b" />
        <ellipse cx={cx + dx + 6} cy={cy - 34} rx={9} ry={5} fill="#e2e8f0" stroke="none" />
      </g>))}
      <rect x={cx - 40} y={cy} width={80} height={30} fill="#94a3b8" />
    </g>
  );
}
function Certificates({ cx, cy }) {
  return (
    <g>
      {[6, 3, 0].map((o, i) => (<rect key={i} x={cx - 44 + o} y={cy - 22 + o} width={80} height={44} rx={3} fill="#fefce8" stroke={C.line} strokeWidth="1.2" />))}
      {[8, 14, 20, 26].map((yy, i) => (<line key={i} x1={cx - 30} y1={cy - 22 + yy} x2={cx + 10} y2={cy - 22 + yy} stroke="#a8a29e" strokeWidth="1.4" />))}
      <circle cx={cx + 22} cy={cy + 6} r="9" fill={C.seal} />
      <rect x={cx + 18} y={cy + 12} width={3} height={16} fill={C.yellow} />
      <rect x={cx + 22} y={cy + 12} width={3} height={16} fill={C.blue} />
      <rect x={cx + 26} y={cy + 12} width={3} height={16} fill={C.green} />
    </g>
  );
}

function Building({ cx, baseY, cols, rows }) {
  const winW = 6, winH = 8, cellW = 10, cellH = 13, padX = 6, padTop = 8, doorH = 10;
  const bw = cols * cellW + 8;
  const bh = rows * cellH + 19;
  const x = cx - bw / 2, y = baseY - bh;
  const wins = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      wins.push([x + padX + c * cellW, y + padTop + r * cellH]);
  return (
    <g stroke="#6b7280" strokeWidth="1.1">
      <rect x={x - 2} y={y - 4} width={bw + 4} height={6} fill="#cbd5e1" />
      <rect x={x} y={y} width={bw} height={bh} fill="#e2e8f0" />
      {wins.map(([wx, wy], i) => (<rect key={i} x={wx} y={wy} width={winW} height={winH} fill="#334155" stroke="none" />))}
      <rect x={cx - 5} y={baseY - doorH} width={10} height={doorH} fill="#5b4636" stroke="none" />
    </g>
  );
}

function Defs() {
  return (
    <defs>
      <marker id="ah-black" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill={C.black} /></marker>
      <marker id="ah-green" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill={C.greenDark} /></marker>
      <marker id="ah-red" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill={C.red} /></marker>
      <marker id="ah-blue" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill={C.blue} /></marker>
    </defs>
  );
}

/* ---- org trees: empty ranked boxes (no names / no titles) ---------------- */
function PlaneCrew() {
  return (
    <g>
      {link(0, 13, -105, 57, C.line)}{link(0, 13, 0, 57, C.line)}{link(0, 13, 105, 57, C.line)}
      {link(105, 83, 60, 127, C.line)}{link(105, 83, 150, 127, C.line)}
      <TNode x={0} y={0} rank={1} />
      <TNode x={-105} y={70} rank={2} /><TNode x={0} y={70} rank={2} /><TNode x={105} y={70} rank={2} />
      <TNode x={60} y={140} rank={3} /><TNode x={150} y={140} rank={3} />
    </g>
  );
}
function BoatCrew() {
  return (
    <g>
      {link(0, 13, -165, 57, C.line)}{link(0, 13, -55, 57, C.line)}{link(0, 13, 55, 57, C.line)}{link(0, 13, 165, 57, C.line)}
      {link(-165, 83, -210, 127, C.line)}{link(-165, 83, -165, 127, C.line)}{link(-165, 83, -120, 127, C.line)}
      {link(-55, 83, -55, 127, C.line)}
      {link(165, 83, 125, 127, C.line)}{link(165, 83, 205, 127, C.line)}
      <TNode x={0} y={0} rank={1} />
      <TNode x={-165} y={70} rank={2} /><TNode x={-55} y={70} rank={2} /><TNode x={55} y={70} rank={2} /><TNode x={165} y={70} rank={2} />
      <TNode x={-210} y={140} rank={4} /><TNode x={-165} y={140} rank={4} /><TNode x={-120} y={140} rank={4} />
      <TNode x={-55} y={140} rank={4} />
      <TNode x={125} y={140} rank={3} /><TNode x={205} y={140} rank={3} />
    </g>
  );
}
function Household() {
  return (
    <g>
      {link(0, 13, -95, 57, C.line)}{link(0, 13, 95, 57, C.line)}
      {link(-95, 83, -150, 127, C.line)}{link(-95, 83, -40, 127, C.line)}
      {link(-150, 153, -192, 197, C.line)}{link(-150, 153, -150, 197, C.line)}{link(-150, 153, -108, 197, C.line)}
      {link(-40, 153, -82, 197, C.line)}{link(-40, 153, -40, 197, C.line)}{link(-40, 153, 2, 197, C.line)}
      {link(95, 83, 55, 127, C.line)}{link(95, 83, 150, 127, C.line)}
      <TNode x={0} y={0} rank={1} />
      <TNode x={-95} y={70} rank={2} /><TNode x={95} y={70} rank={2} />
      <TNode x={-150} y={140} rank={3} /><TNode x={-40} y={140} rank={3} /><TNode x={55} y={140} rank={3} /><TNode x={150} y={140} rank={3} />
      {[-192, -150, -108].map((x, i) => <TNode key={`a${i}`} x={x} y={210} rank={4} />)}
      {[-82, -40, 2].map((x, i) => <TNode key={`b${i}`} x={x} y={210} rank={4} />)}
    </g>
  );
}

/* ---- main --------------------------------------------------------------- */
export default function SolutionsInfographic({ t, onNavigate }) {
  // horizontal shift that centres the whole Familigi->SEBAA column on the page
  const DX = 300;
  // Currencida sits on the west, BoxAlong on the east, both on the same level.
  const boxAlong = { x: 1095, y: 698 };
  const currencida = { x: 150, y: 698 };
  // SEBAA's absolute position after the central column is shifted by DX
  const sebaa = { x: 360 + DX, y: 868 };
  return (
    <div className="not-prose relative left-1/2 my-10 w-[96vw] max-w-[1320px] -translate-x-1/2 rounded-2xl border border-black/10 bg-white p-3 shadow-sm"
      data-testid="solutions-infographic">
      <TransformWrapper initialScale={1} minScale={1} maxScale={6} centerOnInit
        wheel={{ disabled: true }} doubleClick={{ mode: "zoomIn", step: 0.7 }}
        pinch={{ step: 6 }} limitToBounds>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="pointer-events-none absolute right-5 top-5 z-10 flex flex-col gap-1.5">
              <button type="button" onClick={() => zoomIn()} aria-label="Zoom in" data-testid="infographic-zoom-in"
                className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/90 text-slate-700 shadow-sm backdrop-blur transition hover:bg-white active:scale-95"><Plus size={16} /></button>
              <button type="button" onClick={() => zoomOut()} aria-label="Zoom out" data-testid="infographic-zoom-out"
                className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/90 text-slate-700 shadow-sm backdrop-blur transition hover:bg-white active:scale-95"><Minus size={16} /></button>
              <button type="button" onClick={() => resetTransform()} aria-label="Reset zoom" data-testid="infographic-zoom-reset"
                className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/90 text-slate-700 shadow-sm backdrop-blur transition hover:bg-white active:scale-95"><Maximize2 size={16} /></button>
            </div>
            <TransformComponent wrapperStyle={{ width: "100%", height: "auto", cursor: "grab" }} contentStyle={{ width: "100%" }}>
      <svg viewBox="0 -100 1320 1150" role="img"
        aria-label="Money-flow diagram: holdings feed BoxAlong (red), which flows west to Currencida then to SEBAA (black/green); SEBAA's green stream splits four ways to the family tree and the household, boat and air crews, all grouped under Familigi by black power bars."
        style={{ width: "100%", height: "auto", display: "block" }}>
        <Defs />

        {/* framing blue box with the FopFo logo + live URL across the top */}
        <rect x={14} y={-90} width={1292} height={1112} rx={18} fill="#eff6ff" stroke={C.blue} strokeWidth="4" />
        <text x={600} y={-52} textAnchor="middle" fontSize="44" fontWeight="900" fill={C.blue} letterSpacing="1"
          onClick={onNavigate ? () => onNavigate("suite") : undefined}
          data-testid={onNavigate ? "infographic-node-suite" : undefined}
          style={onNavigate ? { cursor: "pointer" } : undefined}>
          {onNavigate && <title>Open the full FopFo suite</title>}
          <tspan style={onNavigate ? { textDecoration: "underline" } : undefined}>FopFo</tspan><tspan fontSize="18" dy="-15" fontWeight="700" style={{ textDecoration: "none" }}>™</tspan></text>
        <a href="https://www.gcafopfo.simprime-ca.com" target="_blank" rel="noopener noreferrer" data-testid="infographic-live-url">
          <title>Open the live FopFo app in a new tab</title>
          <text x={600} y={-18} textAnchor="middle" fontSize="20" fontWeight="600" fill="#000000"
            style={{ textDecoration: "underline", cursor: "pointer" }}>[ https://www.gcafopfo.simprime-ca.com ]</text>
        </a>
        <line x1={40} y1={4} x2={1280} y2={4} stroke={C.blue} strokeWidth="1.5" opacity="0.35" />

        {/* ===== centred Familigi -> SEBAA column (shifted by DX) ============ */}
        <g transform={`translate(${DX},0)`}>
          {/* Familigi and the four elements grouped beneath it (black) */}
          <LogoNode cx={300} cy={82} app="familigi" label={"Familigi\u2122"} labelBelow={false} onNavigate={onNavigate} />
          {/* black power bars: Familigi -> family tree -> bar -> 3 crew trees */}
          <line x1={300} y1={130} x2={300} y2={166} stroke={C.black} strokeWidth="4" />
          <line x1={310} y1={272} x2={310} y2={322} stroke={C.black} strokeWidth="4" />
          <line x1={110} y1={322} x2={580} y2={322} stroke={C.black} strokeWidth="4" />
          <line x1={110} y1={322} x2={110} y2={356} stroke={C.black} strokeWidth="3" />
          <line x1={335} y1={322} x2={335} y2={356} stroke={C.black} strokeWidth="3" />
          <line x1={580} y1={322} x2={580} y2={356} stroke={C.black} strokeWidth="3" />

          {/* the big family tree, immediately below Familigi */}
          <rect x={40} y={166} width={560} height={104} rx={8} fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.4" />
          <image href="/downloads/family_tree_preview.png" x={46} y={170} width={548} height={96} preserveAspectRatio="xMidYMid meet" />

          {/* three crew trees reading across, each with its icon below */}
          <g transform="translate(110,356) scale(0.45)"><PlaneCrew /></g>
          <g transform="translate(335,356) scale(0.45)"><Household /></g>
          <g transform="translate(580,356) scale(0.45)"><BoatCrew /></g>
          <g transform="translate(110 505) scale(0.5) translate(-110 -505)"><Plane cx={110} cy={505} /></g>
          <g transform="translate(335 505) scale(0.5) translate(-335 -505)"><StatelyHome cx={335} cy={505} /></g>
          <g transform="translate(580 505) scale(0.5) translate(-580 -505)"><Boat cx={580} cy={505} /></g>

          {/* SEBAA at the base of the centred column */}
          <LogoNode cx={360} cy={868} app="sebaa" label={"SEBAA\u2122"} labelBelow={true} onNavigate={onNavigate} />

          {/* SEBAA green stream rises and divides four ways to the elements */}
          <line x1={360} y1={820} x2={360} y2={640} stroke={C.greenDark} strokeWidth="6" />
          {/* to the three crew icons */}
          <line x1={360} y1={640} x2={113} y2={540} stroke={C.greenDark} strokeWidth="3" markerEnd="url(#ah-green)" />
          <line x1={360} y1={640} x2={335} y2={540} stroke={C.greenDark} strokeWidth="3" markerEnd="url(#ah-green)" />
          <line x1={360} y1={640} x2={578} y2={540} stroke={C.greenDark} strokeWidth="3" markerEnd="url(#ah-green)" />
          {/* fourth branch forks above the black arrow band, then up the left margin */}
          <path d="M 360 662 H 26 V 218 H 38" fill="none" stroke={C.greenDark} strokeWidth="3" markerEnd="url(#ah-green)" />
        </g>

        {/* ===== money chain: BoxAlong (east) -> Currencida (west) -> SEBAA == */}
        <LogoNode cx={currencida.x} cy={currencida.y} app="currencida" label={"Currencida\u2122"} labelBelow={false} onNavigate={onNavigate} />
        <LogoNode cx={boxAlong.x} cy={boxAlong.y} app="boxalong" label={"BoxAlong\u2122"} labelBelow={false} onNavigate={onNavigate} />

        {/* 3 black arrows: BoxAlong -> Currencida (same level, across the page) */}
        {[-14, 0, 14].map((o, i) => (
          <line key={i} x1={boxAlong.x - 48} y1={boxAlong.y + o} x2={currencida.x + 50} y2={currencida.y + o}
            stroke={C.black} strokeWidth="2" markerEnd="url(#ah-black)" />
        ))}
        {/* 2 blue arrows: leave Currencida's base, drop, then right-angle to SEBAA */}
        <path d={`M ${currencida.x - 8} ${currencida.y + 46} V 858 H ${sebaa.x - 52}`}
          fill="none" stroke={C.blue} strokeWidth="2.4" markerEnd="url(#ah-blue)" />
        <path d={`M ${currencida.x + 8} ${currencida.y + 46} V 874 H ${sebaa.x - 52}`}
          fill="none" stroke={C.blue} strokeWidth="2.4" markerEnd="url(#ah-blue)" />

        {/* ===== 5 holdings to the right of BoxAlong, red arrows to BoxAlong == */}
        {[
          { C: StatelyHome, y: 566, ty: 660, ox: 1196 }, { C: Plane, y: 632, ty: 676, ox: 1190 },
          { C: Boat, y: 698, ty: 692, ox: 1205 }, { C: Factory, y: 764, ty: 708, ox: 1220 },
          { C: Certificates, y: 830, ty: 724, ox: 1218 },
        ].map((a, i) => {
          const cx = 1240;
          const Icon = a.C;
          return (
            <g key={i}>
              <line x1={a.ox} y1={a.y} x2={boxAlong.x + 47} y2={a.ty}
                stroke={C.red} strokeWidth="2" markerEnd="url(#ah-red)" />
              <g transform={`translate(${cx} ${a.y}) scale(0.5) translate(${-cx} ${-a.y})`}>
                <Icon cx={cx} cy={a.y} />
              </g>
            </g>
          );
        })}

        {/* ===== real-estate holdings below the stock certificate ============ */}
        {/* short (6x3) -> medium (3x4) -> tall (3x6), reading left to right */}
        {[
          { cx: 1110, cols: 6, rows: 3, tx: 1072 },
          { cx: 1198, cols: 3, rows: 4, tx: 1095 },
          { cx: 1270, cols: 3, rows: 6, tx: 1118 },
        ].map((b, i) => {
          const top = 1000 - (b.rows * 13 + 19);
          return (
            <g key={`re-${i}`}>
              <line x1={b.cx} y1={top} x2={b.tx} y2={boxAlong.y + 46}
                stroke={C.red} strokeWidth="2" markerEnd="url(#ah-red)" />
              <Building cx={b.cx} baseY={1000} cols={b.cols} rows={b.rows} />
            </g>
          );
        })}
      </svg>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
      <p className="mt-2 text-center text-xs text-slate-500 sm:hidden" data-testid="infographic-zoom-hint">
        Pinch to zoom · drag to pan · double-tap to zoom in
      </p>
      <div className="mt-3 flex justify-center">
        <a href={`${API}/download/diagram?v=${encodeURIComponent(getVisitorId())}`} download="FopFo-diagram.jpg"
          className="inline-flex items-center gap-2 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
          data-testid="download-diagram-png">
          <Download size={16} /> {(t ? t("download_diagram") : "Download diagram")} (JPEG)
        </a>
      </div>
    </div>
  );
}
